// 📂 frontend/src/components/EmailEditor.tsx
import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '../hooks/useTheme';

interface EmailEditorProps {
  initialContent?: string;
  onChange: (content: string) => void;
  onSaveAsTemplate?: () => void;
  showTemplateButton?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme?: any; // Optional theme override
  placeholder?: string;
  minHeight?: number;
}

const EmailEditor: React.FC<EmailEditorProps> = ({
  initialContent = '',
  onChange,
  onSaveAsTemplate,
  showTemplateButton = false,
  theme: themeOverride,
  placeholder = 'Scrie conținutul email-ului aici...',
  minHeight = 300
}) => {
  const { theme: defaultTheme, currentTheme } = useTheme();
  const theme = themeOverride || defaultTheme.colors;
  
  const [content, setContent] = useState(initialContent);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [history, setHistory] = useState<string[]>([initialContent]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Update content when initialContent changes
  useEffect(() => {
    setContent(initialContent);
    setHistory([initialContent]);
    setHistoryIndex(0);
  }, [initialContent]);

  // Handle content change
  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    onChange(newContent);
    
    // Add to history for undo/redo
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newContent);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Format text with HTML tags
  const formatText = (tag: string, value?: string) => {
    if (!textAreaRef.current) return;

    const textarea = textAreaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let formattedText = '';
    switch (tag) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'link':
        // TODO: Replace with custom modal
        const url = prompt('Introdu URL-ul:', 'https://');
        if (url) {
          formattedText = `<a href="${url}" target="_blank">${selectedText || url}</a>`;
        } else {
          return;
        }
        break;
      case 'color':
        formattedText = `<span style="color: ${value}">${selectedText}</span>`;
        break;
      case 'h1':
      case 'h2':
      case 'h3':
        formattedText = `<${tag}>${selectedText}</${tag}>`;
        break;
      case 'ul':
        const items = selectedText.split('\n').filter(item => item.trim());
        formattedText = `<ul>\n${items.map(item => `  <li>${item}</li>`).join('\n')}\n</ul>`;
        break;
      case 'ol':
        const orderedItems = selectedText.split('\n').filter(item => item.trim());
        formattedText = `<ol>\n${orderedItems.map(item => `  <li>${item}</li>`).join('\n')}\n</ol>`;
        break;
      case 'blockquote':
        formattedText = `<blockquote>${selectedText}</blockquote>`;
        break;
      case 'code':
        formattedText = `<code>${selectedText}</code>`;
        break;
      case 'hr':
        formattedText = '<hr>';
        break;
      default:
        formattedText = selectedText;
    }

    const newContent = 
      textarea.value.substring(0, start) + 
      formattedText + 
      textarea.value.substring(end);

    handleContentChange(newContent);

    // Reset cursor position
    setTimeout(() => {
      textarea.selectionStart = start + formattedText.length;
      textarea.selectionEnd = start + formattedText.length;
      textarea.focus();
    }, 0);
  };

  // Insert template variable
  const insertVariable = (variable: string) => {
    if (!textAreaRef.current) return;

    const textarea = textAreaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const variableText = `{{${variable}}}`;

    const newContent = 
      textarea.value.substring(0, start) + 
      variableText + 
      textarea.value.substring(end);

    handleContentChange(newContent);

    // Reset cursor position
    setTimeout(() => {
      textarea.selectionStart = start + variableText.length;
      textarea.selectionEnd = start + variableText.length;
      textarea.focus();
    }, 0);
  };

  // Undo/Redo functionality
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
      onChange(history[newIndex]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setContent(history[newIndex]);
      onChange(history[newIndex]);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'b':
            e.preventDefault();
            formatText('bold');
            break;
          case 'i':
            e.preventDefault();
            formatText('italic');
            break;
          case 'u':
            e.preventDefault();
            formatText('underline');
            break;
          case 'z':
            e.preventDefault();
            handleUndo();
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
        }
      }
    };

    const textarea = textAreaRef.current;
    if (textarea) {
      textarea.addEventListener('keydown', handleKeyDown);
      return () => textarea.removeEventListener('keydown', handleKeyDown);
    }
  }, [historyIndex, history]);

  const styles = {
    container: {
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      backgroundColor: theme.surface,
      overflow: 'hidden'
    },
    toolbar: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '4px',
      padding: '8px',
      borderBottom: `1px solid ${theme.border}`,
      backgroundColor: theme.background
    },
    toolbarGroup: {
      display: 'flex',
      gap: '2px',
      alignItems: 'center',
      paddingRight: '8px',
      borderRight: `1px solid ${theme.border}`,
      marginRight: '4px'
    },
    toolbarButton: {
      padding: '6px 10px',
      backgroundColor: 'transparent',
      border: `1px solid transparent`,
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      color: theme.text,
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    activeButton: {
      backgroundColor: theme.primary + '22',
      borderColor: theme.primary
    },
    editor: {
      position: 'relative' as const,
      minHeight: `${minHeight}px`
    },
    textarea: {
      width: '100%',
      minHeight: `${minHeight}px`,
      padding: '12px',
      border: 'none',
      backgroundColor: theme.background,
      color: theme.text,
      fontSize: '14px',
      fontFamily: isHtmlMode ? 'monospace' : 'inherit',
      resize: 'vertical' as const,
      outline: 'none'
    },
    preview: {
      padding: '12px',
      minHeight: `${minHeight}px`,
      backgroundColor: theme.background,
      color: theme.text,
      overflow: 'auto'
    },
    statusBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      borderTop: `1px solid ${theme.border}`,
      fontSize: '12px',
      color: theme.textSecondary,
      backgroundColor: theme.surface
    },
    colorPicker: {
      marginLeft: '4px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer'
    }
  };

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        {/* Text formatting */}
        <div style={styles.toolbarGroup}>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('bold')}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('italic')}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('underline')}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>
        </div>

        {/* Headings */}
        <div style={styles.toolbarGroup}>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('h1')}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('h2')}
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('h3')}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        {/* Lists and quotes */}
        <div style={styles.toolbarGroup}>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('ul')}
            title="Unordered List"
          >
            • List
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('ol')}
            title="Ordered List"
          >
            1. List
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('blockquote')}
            title="Quote"
          >
            " "
          </button>
        </div>

        {/* Links and special */}
        <div style={styles.toolbarGroup}>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('link')}
            title="Insert Link"
          >
            🔗
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('code')}
            title="Code"
          >
            {'</>'}
          </button>
          <button
            type="button"
            style={styles.toolbarButton}
            onClick={() => formatText('hr')}
            title="Horizontal Rule"
          >
            ―
          </button>
          <input
            type="color"
            style={styles.colorPicker}
            onChange={(e) => formatText('color', e.target.value)}
            title="Text Color"
          />
        </div>

        {/* Variables */}
        <div style={styles.toolbarGroup}>
          <select
            onChange={(e) => {
              if (e.target.value) {
                insertVariable(e.target.value);
                e.target.value = '';
              }
            }}
            style={{
              ...styles.toolbarButton,
              paddingRight: '20px'
            }}
          >
            <option value="">+ Variabilă</option>
            <option value="nume">{'{'}nume{'}'}</option>
            <option value="email">{'{'}email{'}'}</option>
            <option value="data">{'{'}data{'}'}</option>
            <option value="companie">{'{'}companie{'}'}</option>
            <option value="custom">{'{'}custom{'}'}</option>
          </select>
        </div>

        {/* View modes */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
          {showTemplateButton && onSaveAsTemplate && (
            <button
              type="button"
              style={{
                ...styles.toolbarButton,
                backgroundColor: theme.primary,
                color: '#ffffff'
              }}
              onClick={onSaveAsTemplate}
            >
              💾 Salvează ca Template
            </button>
          )}
          <button
            type="button"
            style={{
              ...styles.toolbarButton,
              ...(!isHtmlMode && !showPreview ? styles.activeButton : {})
            }}
            onClick={() => { setIsHtmlMode(false); setShowPreview(false); }}
          >
            ✏️ Edit
          </button>
          <button
            type="button"
            style={{
              ...styles.toolbarButton,
              ...(isHtmlMode ? styles.activeButton : {})
            }}
            onClick={() => { setIsHtmlMode(true); setShowPreview(false); }}
          >
            &lt;/&gt; HTML
          </button>
          <button
            type="button"
            style={{
              ...styles.toolbarButton,
              ...(showPreview ? styles.activeButton : {})
            }}
            onClick={() => { setShowPreview(true); setIsHtmlMode(false); }}
          >
            👁️ Preview
          </button>
        </div>
      </div>

      {/* Editor */}
      <div style={styles.editor}>
        {showPreview ? (
          <div 
            style={styles.preview}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
          />
        ) : isHtmlMode ? (
          <textarea
            ref={textAreaRef}
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder={placeholder}
            style={{ ...styles.textarea, fontFamily: 'monospace', fontSize: '13px' }}
          />
        ) : (
          <div
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => handleContentChange(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            style={{ ...styles.textarea, overflow: 'auto', cursor: 'text', lineHeight: 1.6 }}
          />
        )}
      </div>

      {/* Status bar */}
      <div style={styles.statusBar}>
        <span>
          {content.length} caractere • {content.split(/\s+/).filter(w => w).length} cuvinte
        </span>
        <span>
          {isHtmlMode ? 'Mod HTML' : 'Mod Text'} • 
          {showPreview ? ' Vizualizare Preview' : ' Editare'}
        </span>
      </div>
    </div>
  );
};

export default EmailEditor;